// src/components/Envases/Envases.styles.ts
import styled, { css, createGlobalStyle } from "styled-components";

/* ==================== Paleta ==================== */
const colors = {
  primary: "#0052CC",
  background: "#f8f9fa",
  cardBackground: "#FFFFFF",
  textPrimary: "#172B4D",
  textSecondary: "#6B778C",
  border: "#DFE1E6",
  success: "#28a745",
  danger: "#dc3545",
  warning: "#F8981D",
};

/* ==================== Breakpoints (px) ==================== */
const bp = {
  xl: 1400,
  lg: 1200,
  md: 992,
  sm: 768,
  xs: 576,
  xxs: 420,
};

/* ==================== Layout ==================== */
export const PageContainer = styled.div`
  background-color: ${colors.background};
  padding: 40px 40px;
  min-height: 100%;

  @media (max-width: ${bp.lg}px) { padding: 72px 36px; }
  @media (max-width: ${bp.sm}px) { padding: 56px 24px; }
  @media (max-width: ${bp.xs}px) { padding: 40px 16px; }
`;

export const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: ${bp.sm}px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

export const Title = styled.h1`
  font-size: clamp(20px, 2.2vw, 24px);
  line-height: 1.2;
  font-weight: 600;
  color: ${colors.textPrimary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AddUserButton = styled.button`
  background-color: ${colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out, box-shadow 0.2s ease;

  &:hover {
    background-color: #0041a3;
    box-shadow: 0 4px 12px rgba(0, 82, 204, 0.2);
  }

  @media (max-width: ${bp.sm}px) {
    width: 100%;
    padding: 12px 16px;
  }
`;

/* ==================== Botonera/Acciones ==================== */
export const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;

  button {
    padding: 8px 16px;
    cursor: pointer;
    border: 1px solid ${colors.primary};
    border-radius: 8px;
    background-color: white;
    color: ${colors.primary};
    font-weight: 600;
    transition: background-color .15s ease, color .15s ease;

    @media (max-width: ${bp.sm}px) { flex: 1 1 48%; }
    @media (max-width: ${bp.xxs}px) { flex: 1 1 100%; }
  }

  button.active {
    background-color: ${colors.primary};
    color: white;
  }
`;

/* ==================== Barra de filtros ==================== */
export const FilterBar = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(180px, 1fr));
  gap: 12px;
  align-items: end;
  background: ${colors.cardBackground};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 16px;
  margin: 12px 0 18px 0;

  /* Escalado progresivo */
  @media (max-width: ${bp.xl}px) { grid-template-columns: repeat(4, minmax(160px, 1fr)); }
  @media (max-width: ${bp.lg}px) { grid-template-columns: repeat(3, minmax(160px, 1fr)); }
  @media (max-width: ${bp.md}px) { grid-template-columns: repeat(2, minmax(160px, 1fr)); }
  @media (max-width: ${bp.sm}px) { grid-template-columns: 1fr; }

  /* Botón 'Buscar' responsivo */
  & > button {
    justify-self: start;
    @media (max-width: ${bp.md}px) { justify-self: stretch; }
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0; /* evita overflow de labels largos */
`;

export const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: ${colors.textSecondary};
  letter-spacing: .2px;
`;

/* ==================== Inputs ==================== */
const baseInput = css`
  height: 40px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;
  color: ${colors.textPrimary};
  background: #fff;
  width: 100%;
  min-width: 0;

  &:focus {
    border-color: ${colors.primary};
    box-shadow: 0 0 0 3px rgba(0,82,204,0.12);
  }

  @media (max-width: ${bp.xs}px) {
    height: 44px;
    font-size: 15px;
  }
`;

export const StyledSelect = styled.select`
  ${baseInput}
  appearance: none;
  background-image:
    linear-gradient(45deg, transparent 50%, ${colors.textSecondary} 50%),
    linear-gradient(135deg, ${colors.textSecondary} 50%, transparent 50%);
  background-position: calc(100% - 18px) 50%, calc(100% - 12px) 50%;
  background-size: 6px 6px, 6px 6px;
  background-repeat: no-repeat;
`;

export const StyledInput = styled.input`${baseInput}`;
export const DateInput   = styled.input`${baseInput}`;

/* ==================== DataGrid wrapper ==================== */
export const GridContainer = styled.div`
  background-color: ${colors.cardBackground};
  border-radius: 12px;
  padding: 8px;
  box-shadow: 0 4px 8px -2px rgba(9, 30, 66, 0.1), 0 0 1px rgba(9, 30, 66, 0.08);

  /* Scroll horizontal en móviles si hace falta */
  overflow-x: auto;

  /* Evita que el grid colapse columnas en pantallas estrechas */
  .dx-datagrid {
    min-width: 920px;
  }

  .dx-datagrid-headers {
    border-bottom: 1px solid ${colors.border};
    .dx-header-row {
      color: #000;
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      @media (max-width: ${bp.sm}px) { font-size: 11px; }
    }
  }

  .dx-data-row {
    height: 52px;
    border-bottom: 1px solid ${colors.border} !important;
    color: ${colors.textPrimary};
    &:hover { background-color: #f0f8ff; }
    td { vertical-align: middle; }
  }

  .dx-data-row:last-child { border-bottom: none !important; }

  .dx-command-edit a {
    color: ${colors.textSecondary};
    font-size: 16px;
    margin-right: 8px;
    &:hover { color: ${colors.primary}; }
  }

  .dx-pager {
    padding: 12px 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap; 
    gap: 10px;
    @media (max-width: ${bp.sm}px) { justify-content: center; }
  }

  .dx-page-info {
    color: ${colors.textSecondary};
    font-size: 14px;
    @media (max-width: ${bp.xs}px) { font-size: 13px; }
  }

  .dx-navigate-button { border: none !important; }
`;

/* ==================== Badge de ejemplo (opcional) ==================== */
interface RoleBadgeProps { role: "admin" | "editor" | "viewer"; }

export const RoleBadge = styled.span<RoleBadgeProps>`
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: capitalize;

  ${({ role }) => {
    let color = colors.success;
    let backgroundColor = "#E3FCEF";
    if (role === "admin") { color = colors.danger; backgroundColor = "#FFEBE6"; }
    else if (role === "editor") { color = colors.primary; backgroundColor = "#DEEBFF"; }
    return css` color: ${color}; background-color: ${backgroundColor}; `;
  }}
`;

/* ==================== Estilos globales DevExtreme (popups) ==================== */
export const DevExtremeModalGlobalStyles = createGlobalStyle`
  .dx-popup-wrapper .dx-popup-content {
    border-radius: 12px !important;
    box-shadow: 0 8px 16px -4px rgba(9, 30, 66, 0.25),
      0 0 2px rgba(9, 30, 66, 0.08) !important;
    padding: 0 !important;
  }

  .dx-overlay-content .dx-popup-title {
    padding: 16px 24px;
    border-bottom: 1px solid rgba(0,0,0,0.1);
    background-color: ${colors.warning} !important;
    .dx-toolbar-label { font-size: 18px !important; font-weight: 600 !important; color: #fff !important; }
    .dx-closebutton .dx-icon { color: #fff !important; }
  }

  .dx-popup-content .dx-form { padding: 24px; background-color: #FFFFFF; }
  .dx-form-item > .dx-field-item-label { font-weight: 600 !important; }

  .dx-popup-bottom {
    padding: 16px 24px;
    border-top: 1px solid ${colors.border};
    background-color: #FFFFFF !important;
  }

  .custom-dialog-message {
    font-size: 16px;
    color: ${colors.textPrimary};
    padding: 24px;
    text-align: center;
  }

  .dx-dialog-buttons .dx-button {
    font-weight: 500 !important;
    padding: 8px 20px !important;
    transition: all 0.2s ease-in-out;
  }

  .dx-dialog-buttons .dx-button.dx-button-success {
    background-color: transparent !important;
    border: 1px solid ${colors.success} !important;
    color: ${colors.success} !important;
    padding: 0px 20px !important;
    &:hover { background-color: #e9f7eb !important; color: #1a6e2c !important; }
  }

  .dx-dialog-buttons .dx-button.dx-button-danger {
    background-color: transparent !important;
    border: 1px solid ${colors.danger} !important;
    padding: 0px 20px !important;
    color: ${colors.danger} !important;
    &:hover { background-color: #fcebeb !important; color: #a52121 !important; }
  }

  @media (prefers-reduced-motion: reduce) {
    * { transition: none !important; animation: none !important; }
  }
`;
